import { Button } from '@/components/ui/button';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { UseMutationResult } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { KycFormData } from '../types/kycFormTabs.types';
import { isFormComplete } from '../utils/kycFormTabs.utils';

interface FormProgressIndicatorProps {
  requiredFormIds: string[];
  formData: KycFormData;
  areAllFormsComplete: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  kycFormSubmission: UseMutationResult<any, Error, any, unknown>;
  onRetrySubmit: () => void;
}

/**
 * Component for displaying KYC form completion progress.
 * Shows completed forms count, submission status, and retry button if needed.
 */
export function FormProgressIndicator({
  requiredFormIds,
  formData,
  areAllFormsComplete,
  kycFormSubmission,
  onRetrySubmit,
}: FormProgressIndicatorProps) {
  const { t: tForms } = useFormsTranslation();

  const completedForms = requiredFormIds.filter((formId: string) =>
    isFormComplete(formId, formData, tForms)
  );

  return (
    <div className='flex justify-center mt-4'>
      <div className='text-center'>
        <div className='text-sm text-muted-foreground mb-2'>
          {tForms('kyc.formProgress', {
            completed: completedForms.length,
            total: requiredFormIds.length,
          })}
        </div>
        {areAllFormsComplete ? (
          <div className='flex items-center justify-center text-green-600'>
            {kycFormSubmission.isPending ? (
              <>
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                <span className='text-sm font-medium'>
                  {tForms('kyc.submitting')}
                </span>
              </>
            ) : (
              <span className='text-sm font-medium'>
                {tForms('kyc.allCompletedReady')}
              </span>
            )}
          </div>
        ) : kycFormSubmission.isError ? (
          <div className='flex flex-col items-center justify-center text-red-600'>
            <span className='text-sm font-medium mb-2'>
              {tForms('kyc.submissionFailed', {
                message: kycFormSubmission.error?.message,
              })}
            </span>
            <Button
              variant='outline'
              size='sm'
              onClick={onRetrySubmit}
              disabled={kycFormSubmission.isPending || !areAllFormsComplete}
            >
              {kycFormSubmission.isPending ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  {tForms('kyc.retrying')}
                </>
              ) : (
                tForms('kyc.retrySubmission')
              )}
            </Button>
          </div>
        ) : (
          <div className='text-sm text-muted-foreground'>
            {tForms('kyc.completeFormsToSubmit')}
          </div>
        )}
      </div>
    </div>
  );
}
